import React, { ReactNode, FC, useCallback, MouseEvent } from 'react';

import { WithSpacingProps } from 'ia-react-core/~/hocs/grid/withSpacing';
import Spacing, { SpacingProps } from 'ia-react-core/~/components/grid/Spacing';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import findChildByType from '../../../utils/components/_findChildByType';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';
import CloseButton from '../../buttons/_CloseButton';
import { StyledSection, StyledHeader, StyledContent } from './ClosableSection.styles';
import { Content, Header } from './ClosableSection.compounds';

export interface ClosableSectionProps extends WithSpacingProps, SCP<'section'> {
  /** The block's content */
  children?: ReactNode;
  /** Controls if the closable is opened or not. */
  isOpened?: boolean;
  /** Customizes the label of the close button. */
  closeButtonLabel: string;
  /** Callback triggered when the section closes. */
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
}

export interface ClosableSectionStatics {
  Header: typeof Header;
  Content: typeof Content;
}

export interface ClosableSectionMock {}

const ClosableSectionDefaultProps = {
  isOpened: true,
};

/**
 * @since 1.7.0
 */
const ClosableSection: FC<ClosableSectionProps> & ClosableSectionStatics = ({ children, closeButtonLabel, isOpened, onClose, ...props }) => {
  const header = findChildByType(children, Header);
  const content = excludeChildrenByType(children, Header);

  const SpacingSection: FC<SpacingProps> = useCallback((p) => (
    <Spacing as="section" {...p} />
  ), []);

  if (!isOpened) {
    return null;
  }

  return (
    <StyledSection p-v={24} p-h={16} as={SpacingSection} {...props}>
      <StyledHeader>
        {header}
        <CloseButton label={closeButtonLabel} onClick={onClose} />
      </StyledHeader>
      <StyledContent>{content}</StyledContent>
    </StyledSection>
  );
};

ClosableSection.defaultProps = ClosableSectionDefaultProps;
ClosableSection.Header = Header;
ClosableSection.Content = Content;

export default ClosableSection;
